import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Truck, Receipt, Percent, ShoppingCart, BarChart3 } from 'lucide-react';
import { vendorAccountingApi } from '@/services/vendorAccountingApi';
import toast from 'react-hot-toast';

const fmt = (v: number) =>
    (v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type DashboardStats = Awaited<ReturnType<typeof vendorAccountingApi.getDashboardStats>>;
type Summary = Awaited<ReturnType<typeof vendorAccountingApi.getSummary>>;
type OrderFinancials = Awaited<ReturnType<typeof vendorAccountingApi.getOrderFinancials>>;
type CourierPayments = Awaited<ReturnType<typeof vendorAccountingApi.getCourierPayments>>;

const DATE_PRESETS = [
    { key: 'thisMonth', label: 'Bu Ay' },
    { key: 'lastMonth', label: 'Geçen Ay' },
    { key: 'thisYear', label: 'Bu Yıl' },
    { key: 'custom', label: 'Özel' },
] as const;

function getPresetDates(preset: string) {
    const now = new Date();
    switch (preset) {
        case 'thisMonth':
            return {
                start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
                end: now.toISOString().split('T')[0],
            };
        case 'lastMonth':
            return {
                start: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0],
                end: new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0],
            };
        case 'thisYear':
            return {
                start: new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0],
                end: now.toISOString().split('T')[0],
            };
        default:
            return {
                start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
                end: now.toISOString().split('T')[0],
            };
    }
}

const VendorAccountingPage: React.FC = () => {
    const [activePreset, setActivePreset] = useState('thisMonth');
    const [startDate, setStartDate] = useState(getPresetDates('thisMonth').start);
    const [endDate, setEndDate] = useState(getPresetDates('thisMonth').end);

    const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [orders, setOrders] = useState<OrderFinancials | null>(null);
    const [courierPayments, setCourierPayments] = useState<CourierPayments | null>(null);

    const [loading, setLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [couriersLoading, setCouriersLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'couriers'>('overview');
    const [page, setPage] = useState(1);

    useEffect(() => {
        loadDashboard();
    }, []);

    useEffect(() => {
        loadSummary();
        setPage(1);
        setOrders(null);
        setCourierPayments(null);
    }, [startDate, endDate]);

    useEffect(() => {
        if (activeTab === 'orders') loadOrders();
        if (activeTab === 'couriers') loadCourierPayments();
    }, [activeTab, startDate, endDate]);

    const handlePresetChange = (preset: string) => {
        setActivePreset(preset);
        if (preset !== 'custom') {
            const dates = getPresetDates(preset);
            setStartDate(dates.start);
            setEndDate(dates.end);
        }
    };

    const loadDashboard = async () => {
        try {
            const data = await vendorAccountingApi.getDashboardStats();
            setDashboardStats(data);
        } catch {
            // Quiet fail
        }
    };

    const loadSummary = async () => {
        setLoading(true);
        try {
            const data = await vendorAccountingApi.getSummary({ startDate, endDate });
            setSummary(data);
        } catch {
            toast.error('Muhasebe özeti yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const loadOrders = async (p = 1) => {
        setOrdersLoading(true);
        try {
            const data = await vendorAccountingApi.getOrderFinancials({ startDate, endDate, page: p, limit: 20 });
            setOrders(data);
            setPage(p);
        } catch {
            toast.error('Sipariş finansalları yüklenemedi');
        } finally {
            setOrdersLoading(false);
        }
    };

    const loadCourierPayments = async () => {
        setCouriersLoading(true);
        try {
            const data = await vendorAccountingApi.getCourierPayments({ startDate, endDate });
            setCourierPayments(data);
        } catch {
            toast.error('Kurye ödeme verileri yüklenemedi');
        } finally {
            setCouriersLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Muhasebe</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">Finansal durumunuzu takip edin</p>
            </div>

            {/* Quick Stats */}
            {dashboardStats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {([
                        { label: 'Bugün', data: dashboardStats.today, color: 'blue' },
                        { label: 'Bu Hafta', data: dashboardStats.thisWeek, color: 'purple' },
                        { label: 'Bu Ay', data: dashboardStats.thisMonth, color: 'green' },
                    ] as const).map(({ label, data, color }) => (
                        <div key={label} className={`bg-${color}-50 dark:bg-${color}-900/20 p-4 rounded-lg shadow`}>
                            <p className={`text-sm font-medium text-${color}-600 dark:text-${color}-400`}>{label}</p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">₺{fmt(data.revenue)}</p>
                            <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                                <span>{data.orders} sipariş</span>
                                <span className={data.netEarnings >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    Net: ₺{fmt(data.netEarnings)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Date Presets + Custom */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex flex-wrap gap-2 items-center">
                    {DATE_PRESETS.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => handlePresetChange(key)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                activePreset === key
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                    {activePreset === 'custom' && (
                        <div className="flex gap-2 ml-4">
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm" />
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm" />
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="flex gap-4">
                    {([
                        { key: 'overview', label: 'Genel Bakış', icon: BarChart3 },
                        { key: 'orders', label: 'Sipariş Detayları', icon: Receipt },
                        { key: 'couriers', label: 'Kurye Ödemeleri', icon: Truck },
                    ] as const).map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            onClick={() => setActiveTab(key)}
                            className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium transition ${
                                activeTab === key
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <Icon size={16} />
                            {label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                loading ? (
                    <div className="flex justify-center py-12"><p>Yükleniyor...</p></div>
                ) : summary ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <MetricCard icon={DollarSign} label="Toplam Ciro" value={`₺${fmt(summary.totalRevenue)}`} color="blue" />
                            <MetricCard icon={TrendingUp} label="Net Kazanç" value={`₺${fmt(summary.netEarnings)}`} color={summary.netEarnings >= 0 ? 'green' : 'red'} />
                            <MetricCard icon={Truck} label="Kurye Gideri" value={`₺${fmt(summary.totalCourierFees)}`} color="orange" />
                            <MetricCard icon={Percent} label={`Komisyon (%${summary.commissionRate})`} value={`₺${fmt(summary.totalCommission)}`} color="purple" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <MetricCard icon={ShoppingCart} label="Sipariş Sayısı" value={String(summary.orderCount)} color="indigo" />
                            <MetricCard icon={BarChart3} label="Ort. Sipariş Değeri" value={`₺${fmt(summary.averageOrderValue)}`} color="cyan" />
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Kâr Marjı</p>
                                <p className={`text-3xl font-bold mt-2 ${summary.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    %{summary.profitMargin.toFixed(1)}
                                </p>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-3">
                                    <div
                                        className={`h-2 rounded-full ${summary.profitMargin >= 0 ? 'bg-green-600' : 'bg-red-600'}`}
                                        style={{ width: `${Math.min(Math.max(summary.profitMargin, 0), 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null
            )}

            {activeTab === 'orders' && (
                ordersLoading ? (
                    <div className="flex justify-center py-12"><p>Yükleniyor...</p></div>
                ) : orders ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sipariş</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ciro</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Kurye</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Komisyon</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Net</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tarih</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {orders.orders.map((o) => (
                                    <tr key={o.orderId} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                                        <td className="px-4 py-3">
                                            <span className="font-medium text-gray-800 dark:text-gray-200">#{o.orderNo}</span>
                                            {o.courierName && <p className="text-xs text-gray-500 mt-0.5">{o.courierName}</p>}
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-gray-200">₺{fmt(o.totalRevenue)}</td>
                                        <td className="px-4 py-3 text-right text-orange-600">-₺{fmt(o.courierFee)}</td>
                                        <td className="px-4 py-3 text-right text-purple-600">-₺{fmt(o.vendorCommission)}</td>
                                        <td className={`px-4 py-3 text-right font-bold ${o.netEarnings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            ₺{fmt(o.netEarnings)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {o.reportedDate ? new Date(o.reportedDate).toLocaleDateString('tr-TR') : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {orders.hasMore && (
                            <div className="p-4 text-center">
                                <button onClick={() => loadOrders(page + 1)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                                    Daha Fazla Yükle
                                </button>
                            </div>
                        )}
                    </div>
                ) : null
            )}

            {activeTab === 'couriers' && (
                couriersLoading ? (
                    <div className="flex justify-center py-12"><p>Yükleniyor...</p></div>
                ) : courierPayments ? (
                    <div className="space-y-4">
                        {/* Courier Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <MetricCard icon={DollarSign} label="Toplam Ödenen" value={`₺${fmt(courierPayments.summary.totalPaid)}`} color="orange" />
                            <MetricCard icon={Truck} label="Toplam Teslimat" value={String(courierPayments.summary.totalDeliveries)} color="blue" />
                            <MetricCard icon={BarChart3} label="Ort. Teslimat Başına" value={`₺${fmt(courierPayments.summary.averagePerDelivery)}`} color="green" />
                        </div>
                        {/* Courier Breakdown */}
                        {courierPayments.couriers.map((c) => (
                            <div key={c.courierId} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                <div className="flex justify-between items-center mb-3">
                                    <div>
                                        <p className="font-semibold text-gray-800 dark:text-gray-200">{c.courierName}</p>
                                        <p className="text-sm text-gray-500">{c.deliveryCount} teslimat</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-bold text-orange-600">₺{fmt(c.totalPaid)}</p>
                                        <p className="text-xs text-gray-500">Ort: ₺{fmt(c.averagePerDelivery)}</p>
                                    </div>
                                </div>
                                <details className="text-sm">
                                    <summary className="cursor-pointer text-blue-600 hover:text-blue-800">Teslimat detayları</summary>
                                    <div className="mt-2 space-y-1">
                                        {c.deliveries.map((d) => (
                                            <div key={d.orderId} className="flex justify-between text-gray-600 dark:text-gray-400 py-1 border-b border-gray-100 dark:border-gray-700">
                                                <span>#{d.orderNo}</span>
                                                <div className="flex gap-4">
                                                    <span>₺{fmt(d.amount)}</span>
                                                    <span className="text-gray-400">{d.deliveredAt ? new Date(d.deliveredAt).toLocaleDateString('tr-TR') : '-'}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            </div>
                        ))}
                        {courierPayments.couriers.length === 0 && (
                            <p className="text-center text-gray-500 py-8">Bu dönemde kurye ödemesi yok</p>
                        )}
                    </div>
                ) : null
            )}
        </div>
    );
};

const MetricCard: React.FC<{
    icon: React.ElementType;
    label: string;
    value: string;
    color: string;
}> = ({ icon: Icon, label, value, color }) => (
    <div className={`bg-${color}-50 dark:bg-${color}-900/20 p-6 rounded-lg shadow`}>
        <div className="flex items-center justify-between">
            <div>
                <p className={`text-sm text-${color}-600 dark:text-${color}-400`}>{label}</p>
                <p className={`text-2xl font-bold text-${color}-700 dark:text-${color}-300 mt-2`}>{value}</p>
            </div>
            <Icon className={`text-${color}-600`} size={28} />
        </div>
    </div>
);

export default VendorAccountingPage;
